import { NgTemplateOutlet } from '@angular/common';
import {
  afterNextRender,
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  Injector,
  input,
  InputSignal,
  output,
  OutputEmitterRef,
  Signal,
  signal,
  viewChild,
  WritableSignal
} from '@angular/core';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { TaskCardsListComponent } from '@app/components/task/task-cards-list/task-cards-list.component';
import { TaskFiltersService } from '@app/components/task/task-list-toolbar/task-filters.service';
import { TaskSortControlsComponent } from '@app/components/task/task-list-toolbar/task-sort-controls/task-sort-controls.component';
import { TaskDescriptionDraftService } from '@app/components/task/task-splitview-list/task-details-content/task-description-draft.service';
import { TaskDetailsContentComponent } from '@app/components/task/task-splitview-list/task-details-content/task-details-content.component';
import { TaskDetailsTab } from '@app/components/task/task-splitview-list/task-details-content/task-details-tab.enum';
import { TaskDetailsViewSettingsService } from '@app/components/task/task-splitview-list/task-details-content/task-details-view-settings.service';
import { TaskTableComponent } from '@app/components/task/task-table/task-table.component';
import { TaskTableSortChange } from '@app/components/task/task-table/task-table.utils';
import { TaskListViewMode } from '@app/shared/types/models/task/task-schedule.model';
import { UiButtonComponent } from '@app/shared/ui/button/ui-button.component';

import { TasksActionsService } from '@app/shared/services/actions/tasks-actions.service';
import { DeviceService } from '@app/shared/services/shared/device.service';
import { TaskTypesDataService } from '@app/shared/services/task-types-data.service';
import { TaskPriority } from '@app/shared/types/enums/task-priority.enum';
import { TaskStatus } from '@app/shared/types/enums/task-status.enum';
import { TaskActivity } from '@app/shared/types/models/task-activities/task-activity';
import { TaskType } from '@app/shared/types/models/task/task-type.model';
import { Task } from '@app/shared/types/models/task/task.model';
import { TasksPageData } from '@app/shared/types/models/tasks-page-data';
import { resolveAutoExpandedAncestorIds } from '@app/shared/utils/task-ancestor-path.util';
import { ActiveTaskService } from '@app/views/company/projects/tasks/active-task.service';
import { TasksListActionsService } from '@app/views/company/projects/tasks/tasks-list-actions.service';
import { SplitAreaComponent, SplitComponent, SplitGutterInteractionEvent } from 'angular-split';
import { TaskTimelineComponent } from '../task-timeline/task-timeline.component';
import {
  taskSplitviewLayout,
  taskSplitviewPaneSizes,
  TaskSplitviewPaneSizes,
  taskSplitviewVisiblePanes
} from './task-splitview-layout.util';

@Component({
  selector: 'proffeo-task-splitview-list',
  templateUrl: './task-splitview-list.component.html',
  styleUrls: ['./task-splitview-list.component.scss'],
  providers: [TaskDetailsViewSettingsService, TaskDescriptionDraftService],
  imports: [
    TaskDetailsContentComponent,
    TaskCardsListComponent,
    TaskSortControlsComponent,
    TaskTableComponent,
    UiButtonComponent,
    SplitComponent,
    SplitAreaComponent,
    NgTemplateOutlet,
    TaskTimelineComponent,
    MatPaginator
  ],
  host: {
    class: 'flex min-w-0 w-full flex-col',
    '[class.h-full]': '!isMobile()',
    '[class.min-h-0]': '!isMobile()'
  }
})
export class TaskSplitviewListComponent {
  private readonly activeTaskService: ActiveTaskService = inject(ActiveTaskService);
  private readonly taskActions: TasksActionsService = inject(TasksActionsService);
  private readonly tasksListActions: TasksListActionsService = inject(TasksListActionsService);
  private readonly deviceService: DeviceService = inject(DeviceService);
  private readonly descriptionDraft = inject(TaskDescriptionDraftService);
  private readonly taskTypesService: TaskTypesDataService = inject(TaskTypesDataService);
  private readonly injector: Injector = inject(Injector);
  private readonly destroyRef = inject(DestroyRef);
  private readonly workspaceWidth = signal(0);
  private readonly historyExplicitlyOpened = signal(false);
  private readonly paneLayout = computed(() =>
    taskSplitviewLayout(this.workspaceWidth(), this.isActivityPanelOpen(), this.historyExplicitlyOpened())
  );
  private readonly visiblePanes = computed(() => taskSplitviewVisiblePanes(this.paneLayout()).join('+'));
  /** Last gutter drag, tagged with its pane set; kept across resizes until panes open/close. */
  private readonly draggedPaneSizes = signal<{ panes: string; sizes: TaskSplitviewPaneSizes } | null>(null);
  private readonly paneSizes = computed(() => {
    const dragged = this.draggedPaneSizes();
    const preferred = dragged?.panes === this.visiblePanes() ? dragged.sizes : null;
    return taskSplitviewPaneSizes(this.paneLayout(), preferred, this.listPaneMinSize);
  });

  private readonly hostElement: ElementRef<HTMLElement> = inject(ElementRef);

  private readonly taskDetailsContent = viewChild<TaskDetailsContentComponent>('taskDetailsContent');

  private readonly taskTable = viewChild(TaskTableComponent);
  private lastDraftTaskId: string | undefined;
  // PROPOZYCJA — DO WERYFIKACJI: local presentation state, never a second task store.
  // List/detail share the page body on mobile; preserve each offset before replacing its content.
  private scrollContext: { mobile: boolean; taskId: string | undefined } | undefined;
  private mobileListScrollTop = 0;
  private mobileDetailScroll: { taskId: string; top: number } | undefined;
  private pendingMobileScrollTop: number | undefined;

  protected readonly taskFiltersState: TaskFiltersService = inject(TaskFiltersService);
  protected readonly isMobile: Signal<boolean> = this.deviceService.isMobile;
  protected readonly showDesktopTable: Signal<boolean> = computed(
    () => this.viewMode() === 'timeline' || (!this.isMobile() && this.viewMode() === 'table')
  );
  protected readonly isActivityPanelOpen = signal(true);
  protected readonly detailsTab = signal<TaskDetailsTab>(TaskDetailsTab.Description);
  protected readonly isActivityPanelVisible = computed(() => this.paneLayout().historyVisible);
  protected readonly detailsPaneMinSize = computed(() => this.paneLayout().detailsMin);
  protected readonly activityPaneMinSize = computed(() => this.paneLayout().historyMin);
  protected readonly listPaneMinSize = 15;
  protected readonly listPaneSize = computed(() => (this.activeTask() ? this.paneSizes().list : 100));
  protected readonly detailsPaneSize = computed(() => this.paneSizes().details);
  protected readonly activityPaneSize = computed(() => this.paneSizes().history);
  protected readonly rootTasks = computed((): Task[] => {
    const page = this.allTasks();
    if (!page?.content) {
      return [];
    }

    return page.content.map(taskSignal => taskSignal()).filter((task): task is Task => !!task);
  });
  protected readonly isCompactPagination: Signal<boolean> = computed(() => this.isMobile() || !!this.activeTask());
  protected readonly expandedNodes: WritableSignal<Set<string>> = signal(new Set<string>());
  protected activeCompanyTaskTypes: Signal<TaskType[]> = this.taskTypesService.getActiveCompanyTaskTypes();

  /** @summary Cards on either device, or table on desktop / compact on mobile. */
  public readonly viewMode: InputSignal<TaskListViewMode> = input<TaskListViewMode>('table');
  public readonly allTasks: InputSignal<TasksPageData> = input<TasksPageData>();
  public readonly isLoading: InputSignal<boolean> = input<boolean>(false);
  public readonly searchText: InputSignal<string | undefined> = input<string | undefined>();
  public readonly searchTextChanged: OutputEmitterRef<string | null> = output();
  public readonly activeTask: Signal<Task> = this.activeTaskService.activeTask;
  public readonly taskActivities: Signal<TaskActivity[]> = this.activeTaskService.activeTaskActivities;
  /** @summary Column-manager menu of the desktop table, for the toolbar settings button. */
  public readonly tableColumnManagerMenu = computed(() => this.taskTable()?.columnManagerMenu());

  public constructor() {
    afterNextRender(() => {
      const host = this.hostElement.nativeElement;
      this.workspaceWidth.set(host.getBoundingClientRect().width);
      if (typeof ResizeObserver === 'undefined') return;
      const observer = new ResizeObserver(entries => {
        this.workspaceWidth.set(entries[0].contentRect.width);
      });
      observer.observe(host);
      this.destroyRef.onDestroy(() => observer.disconnect());
    });

    effect(() => {
      const taskId = this.activeTaskService.activeTaskId();
      if (taskId !== this.lastDraftTaskId) {
        this.descriptionDraft.clearAll();
        this.lastDraftTaskId = taskId;
      }
    });

    effect(() => {
      const rootTaskIds = (this.allTasks()?.content ?? [])
        .map(taskSignal => taskSignal()?.id)
        .filter((taskId): taskId is string => !!taskId);
      const ancestorIdsToExpand = resolveAutoExpandedAncestorIds(this.activeTask(), rootTaskIds);
      if (ancestorIdsToExpand.size === 0) {
        return;
      }

      this.expandedNodes.update(currentExpandedNodes => {
        let changed = false;
        const nextExpandedNodes = new Set(currentExpandedNodes);
        for (const ancestorId of ancestorIdsToExpand) {
          if (!nextExpandedNodes.has(ancestorId)) {
            nextExpandedNodes.add(ancestorId);
            changed = true;
          }
        }
        return changed ? nextExpandedNodes : currentExpandedNodes;
      });
    });

    effect(() => {
      if (!this.activeTask()) {
        this.isActivityPanelOpen.set(true);
        this.historyExplicitlyOpened.set(false);
        this.draggedPaneSizes.set(null);
      }
    });

    effect(onCleanup => {
      const taskId = this.activeTaskService.activeTaskId();
      const mobile = this.isMobile();
      const previous = this.scrollContext;
      const pageBody = this.hostElement.nativeElement.closest<HTMLElement>('.proffeo-ui-page-wrapper-body');
      if (previous?.mobile && pageBody) {
        if (previous.taskId) {
          this.mobileDetailScroll = { taskId: previous.taskId, top: this.pendingMobileScrollTop ?? pageBody.scrollTop };
        } else {
          this.mobileListScrollTop = this.pendingMobileScrollTop ?? pageBody.scrollTop;
        }
      }
      this.scrollContext = { mobile, taskId };
      this.pendingMobileScrollTop = undefined;
      const taskChanged = previous?.taskId !== taskId;
      const mobileTop = taskId
        ? !taskChanged && this.mobileDetailScroll?.taskId === taskId
          ? this.mobileDetailScroll.top
          : 0
        : this.mobileListScrollTop;
      let frame: number | undefined;
      let stopRestoration: (() => void) | undefined;

      const render = afterNextRender(
        () => {
          if (mobile && pageBody) {
            stopRestoration = this.restoreMobileScroll(pageBody, mobileTop);
          } else if (taskId && taskChanged) {
            this.resetDetailsScrollPositions();
            frame = requestAnimationFrame(() => this.resetDetailsScrollPositions());
          }
        },
        { injector: this.injector }
      );
      onCleanup(() => {
        render.destroy();
        stopRestoration?.();
        if (frame !== undefined) cancelAnimationFrame(frame);
      });
    });
  }

  protected onCloseActivityPanel(): void {
    this.isActivityPanelOpen.set(false);
    this.historyExplicitlyOpened.set(false);
    this.draggedPaneSizes.set(null);
  }

  protected onReopenActivityPanel(): void {
    this.historyExplicitlyOpened.set(true);
    this.isActivityPanelOpen.set(true);
    this.draggedPaneSizes.set(null);
  }

  /** Remembers the drag so width-driven `[size]` updates re-fit it instead of resetting to defaults. */
  protected onPaneDragEnd({ sizes }: SplitGutterInteractionEvent): void {
    const panes = taskSplitviewVisiblePanes(this.paneLayout());
    if (sizes.length !== panes.length) return;
    const dragged = { list: 0, details: 0, history: 0 };
    for (const [index, pane] of panes.entries()) {
      const size = sizes[index];
      if (typeof size !== 'number') return;
      dragged[pane] = size;
    }
    this.draggedPaneSizes.set({ panes: panes.join('+'), sizes: dragged });
  }

  /** Restore once when asynchronous content makes the offset reachable; user intent always wins. */
  private restoreMobileScroll(pageBody: HTMLElement, top: number): (() => void) | undefined {
    pageBody.scrollTop = top;
    if (Math.abs(pageBody.scrollTop - top) < 1 || typeof ResizeObserver === 'undefined') return undefined;

    this.pendingMobileScrollTop = top;
    const listeners = new AbortController();
    const stop = (): void => {
      observer.disconnect();
      listeners.abort();
    };
    const cancel = (): void => {
      this.pendingMobileScrollTop = undefined;
      stop();
    };
    const observer = new ResizeObserver(() => {
      if (pageBody.scrollHeight - pageBody.clientHeight < top) return;
      pageBody.scrollTop = top;
      this.pendingMobileScrollTop = undefined;
      stop();
    });
    observer.observe(this.hostElement.nativeElement);
    observer.observe(pageBody);
    for (const event of ['pointerdown', 'touchstart', 'wheel', 'keydown']) {
      pageBody.ownerDocument.addEventListener(event, cancel, {
        capture: true,
        passive: true,
        signal: listeners.signal
      });
    }
    // Cleanup disconnects the observer but retains the pending offset for an immediately following resize.
    return stop;
  }

  private resetDetailsScrollPositions(): void {
    this.taskDetailsContent()?.scrollToTop();

    const pageBody = this.hostElement.nativeElement.closest('.proffeo-ui-page-wrapper-body');
    if (pageBody instanceof HTMLElement) {
      pageBody.scrollTop = 0;
    }
  }

  protected onStatusChanged(event: { status: TaskStatus; task: Task }): void {
    this.taskActions.updateTaskStatus(event.task, event.status);
  }

  protected onPriorityChanged(event: { priority: TaskPriority; task: Task }): void {
    this.taskActions.updateTaskPriority(event.task, event.priority);
  }

  protected onTaskStartDateSelected(event: { date: Date; task: Task }): void {
    this.taskActions.updateTaskStartDate(event.task, event.date);
  }

  protected onTaskEndDateSelected(event: { date: Date; task: Task }): void {
    this.taskActions.updateTaskEndDate(event.task, event.date);
  }

  protected onTaskProgressSelected(event: { progress: number; task: Task }): void {
    this.taskActions.updateTaskProgress(event.task, event.progress);
  }

  protected onIndustryClicked(task: Task): void {
    this.taskActions.openSelectIndustryDialog(task);
  }

  protected onLocationClicked(task: Task): void {
    this.taskActions.openSelectLocationDialog(task);
  }

  protected onChangeGroupClicked(task: Task): void {
    this.taskActions.openSelectGroupDialog(task);
  }

  protected onChangeUsersClicked(task: Task): void {
    this.taskActions.openSelectParticipantsDialog(task);
  }

  protected onTableSortChange(event: TaskTableSortChange): void {
    if (event === 'clear') {
      this.taskFiltersState.resetSort();
      return;
    }

    this.taskFiltersState.setSort(event.orderBy, event.order);
  }

  protected onStartTaskCreation(): void {
    this.tasksListActions.startTaskCreation();
  }

  protected changePage(event: PageEvent): void {
    this.taskFiltersState.setPage(event.pageIndex);
    this.taskFiltersState.setLimit(event.pageSize);
  }
}
